import React from "react";
import { Clock, QrCode, ImagePlus, AlertTriangle } from "lucide-react";
import { useShifts, useOpenShift } from "../hooks";
import {
  usePatrolConfig,
  usePatrolOutboxRecords,
  usePatrolScanSubmit,
  usePatrolStatus,
} from "@/features/patrol/hooks";
import { countUniquePendingInWindow } from "@/features/patrol/offline/patrolScanOutbox";
import { normalizePatrolWindow } from "@/features/patrol/utils/normalizePatrolWindow";
import { resolvePatrolDisplayPeriod } from "@/features/patrol/utils/patrolDisplayPeriod";
import {
  getPatrolImminentThresholdMs,
  PATROL_IMMINENT_FRACTION,
  resolvePatrolEffectiveWindow,
} from "@/features/patrol/utils/resolvePatrolEffectiveWindow";
import { openFilePickerForQr } from "@/features/patrol/utils/decodeQr";
import { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHint } from "@capacitor/barcode-scanner";
import { useResponsive } from "@/hooks/use-responsive";
import { ShiftsList } from "../components/ShiftsList";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/shared/utils/cn";
import { getCurrentPosition } from "@/shared/utils/location";
import { formatTimeInZone } from "../utils/formatShiftTime";
import { useToast } from "@/hooks/use-toast";

type FilterType = "all" | "week" | "month";

export function ShiftsPage() {
  const { data: shifts, isLoading, error } = useShifts();
  const { data: openShift, isLoading: isLoadingOpenShift } = useOpenShift();
  const { data: patrolConfig } = usePatrolConfig();
  const { data: patrolStatus } = usePatrolStatus();
  const { data: patrolOutbox = [] } = usePatrolOutboxRecords();
  const { isDesktop } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { submitPayload, isPending: isPatrolSubmitPending } = usePatrolScanSubmit();
  const [filter, setFilter] = useState<FilterType>("all");
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isPatrolScanning, setIsPatrolScanning] = useState(false);

  const showPatrolCard = patrolConfig != null;

  const patrolRequiredScans = patrolConfig?.min_qr_codes_to_scan ?? 0;
  const patrolStatusRequiredScans = patrolStatus?.min_qr_codes_to_scan ?? patrolRequiredScans;
  const patrolScannedInPeriod = patrolStatus?.unique_scanned_in_period ?? 0;

  const patrolPeriod = useMemo(() => normalizePatrolWindow(patrolStatus), [patrolStatus]);
  const patrolPeriodEndIso = patrolPeriod?.periodEndIso ?? patrolStatus?.window_end ?? "";
  const patrolDisplayPeriod = useMemo(
    () =>
      resolvePatrolDisplayPeriod({
        normalized: patrolPeriod,
        openShift: openShift ?? undefined,
        periodAnchorIso: patrolStatus?.period_started_at,
        fallbackIntervalMinutes: patrolConfig?.patrol_interval_minutes ?? null,
      }),
    [patrolPeriod, openShift, patrolStatus?.period_started_at, patrolConfig?.patrol_interval_minutes]
  );
  const patrolEffective = useMemo(
    () =>
      resolvePatrolEffectiveWindow({
        normalized: patrolPeriod,
        openShift: openShift ?? null,
        intervalMinutes: patrolConfig?.patrol_interval_minutes ?? null,
        resolvedDisplay: patrolDisplayPeriod,
        fallbackStartIso: patrolPeriod?.periodStartIso ?? patrolStatus?.window_start ?? "",
        fallbackDeadlineIso: patrolPeriodEndIso,
      }),
    [
      patrolPeriod,
      openShift,
      patrolConfig?.patrol_interval_minutes,
      patrolDisplayPeriod,
      patrolPeriodEndIso,
      patrolStatus?.window_start,
    ]
  );
  const patrolTz = openShift?.timezone ?? "UTC";
  const patrolDisplayStartIso = patrolEffective.displayStartIso;
  const patrolDisplayEndIso = patrolEffective.displayEndIso;
  const pendingUniqueInDisplayWindow = useMemo(() => {
    if (!patrolDisplayStartIso || !patrolDisplayEndIso) return 0;
    return countUniquePendingInWindow(patrolOutbox, patrolDisplayStartIso, patrolDisplayEndIso);
  }, [patrolOutbox, patrolDisplayStartIso, patrolDisplayEndIso]);
  const patrolEffectiveScanned = patrolScannedInPeriod + pendingUniqueInDisplayWindow;
  const patrolProgressPercent =
    patrolStatusRequiredScans > 0
      ? Math.min(100, (100 * patrolEffectiveScanned) / patrolStatusRequiredScans)
      : 0;
  const patrolDisplaySpanMinutes = useMemo(() => {
    if (!patrolDisplayStartIso || !patrolDisplayEndIso) return null;
    const a = new Date(patrolDisplayStartIso).getTime();
    const b = new Date(patrolDisplayEndIso).getTime();
    if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return null;
    return Math.round((b - a) / 60_000);
  }, [patrolDisplayStartIso, patrolDisplayEndIso]);
  const patrolWindowEnd =
    patrolEffective.deadlineIso !== ""
      ? new Date(patrolEffective.deadlineIso)
      : null;
  const patrolRemainingMs =
    patrolWindowEnd != null && !Number.isNaN(patrolWindowEnd.getTime())
      ? patrolWindowEnd.getTime() - Date.now()
      : null;
  const patrolIsComplete =
    patrolStatus != null && patrolStatus.min_qr_codes_to_scan > 0
      ? patrolEffectiveScanned >= patrolStatus.min_qr_codes_to_scan
      : false;

  /** Imminent/overdue UI only when a shift is open (status window can be stale after clock-out). */
  const isPatrolOverdue =
    openShift != null &&
    patrolStatus != null &&
    !patrolIsComplete &&
    patrolRemainingMs != null &&
    patrolRemainingMs < 0;

  const patrolImminentThresholdMs = getPatrolImminentThresholdMs(
    patrolConfig?.patrol_interval_minutes ?? 0
  );
  const isPatrolImminent =
    openShift != null &&
    patrolStatus != null &&
    !patrolIsComplete &&
    patrolRemainingMs != null &&
    patrolRemainingMs >= 0 &&
    patrolImminentThresholdMs != null &&
    patrolRemainingMs <= patrolImminentThresholdMs;

  const handlePatrolScanQr = async () => {
    if (isPatrolScanning || isPatrolSubmitPending) return;
    setIsPatrolScanning(true);
    try {
      // E2E: allow Playwright to inject a scan payload so the full flow can be tested without the camera
      const win = typeof window !== "undefined" ? (window as Window & { __E2E_PATROL_SCAN_PAYLOAD?: string }) : undefined;
      const e2ePayload = win?.__E2E_PATROL_SCAN_PAYLOAD;
      if (e2ePayload != null) {
        if (win) delete win.__E2E_PATROL_SCAN_PAYLOAD;
        await submitPayload(e2ePayload.trim());
        return;
      }
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
        scanInstructions: "Position the QR code within the frame",
        // scanButton: true,
        // scanText: "Scan QR code",
      });
      const payload = result?.ScanResult?.trim();
      if (payload) {
        await submitPayload(payload);
      } else {
        toast({
          title: "No QR code found",
          description: "Try again or choose an image from your gallery.",
          variant: "destructive",
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Scan was cancelled or failed";
      if (!message.toLowerCase().includes("cancel")) {
        toast({
          title: "Scan failed",
          description: message,
          variant: "destructive",
        });
      }
    } finally {
      setIsPatrolScanning(false);
    }
  };

  /**
   * When arriving from a patrol overdue notification, `NotificationAlert` passes
   * `location.state.openPatrolScanner = true`, or `navigationService` uses
   * `/shifts?openPatrolScanner=1`. We auto-open the scanner once and clear
   * state / query so it does not re-fire.
   */
  const didAutoOpenScannerRef = useRef(false);
  const openPatrolScannerFromState =
    (location.state as { openPatrolScanner?: boolean } | null)?.openPatrolScanner === true;
  const openPatrolScannerFromQuery = searchParams.get("openPatrolScanner") === "1";
  const openPatrolScannerRequested =
    openPatrolScannerFromState || openPatrolScannerFromQuery;
  useEffect(() => {
    if (!openPatrolScannerRequested) return;
    if (didAutoOpenScannerRef.current) return;
    if (!showPatrolCard) return;
    didAutoOpenScannerRef.current = true;
    navigate({ pathname: location.pathname, search: "" }, { replace: true, state: null });
    void handlePatrolScanQr();
    // handlePatrolScanQr is stable enough for this one-shot trigger; we
    // intentionally omit it from deps to avoid re-running on re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPatrolScannerRequested, showPatrolCard, location.pathname, navigate, searchParams]);

  const handlePatrolChooseGallery = async () => {
    if (isPatrolSubmitPending) return;
    const payload = await openFilePickerForQr();
    if (payload) {
      await submitPayload(payload);
    } else {
      toast({
        title: "No QR code found",
        description: "The image didn't contain a valid QR code.",
        variant: "destructive",
      });
    }
  };

  // Filter shifts based on selected filter
  const filteredShifts = useMemo(() => {
    if (!shifts) return [];
    
    if (filter === "all") {
      return shifts;
    }
    
    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today
    let startDate: Date;
    
    switch (filter) {
      case "week":
        // Last 7 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "month":
        // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        return shifts;
    }
    
    return shifts.filter((shift) => {
      const shiftDate = new Date(shift.started_at);
      return shiftDate >= startDate && shiftDate <= now;
    });
  }, [shifts, filter]);

  // Calculate statistics based on filtered shifts
  const totalShifts = useMemo(() => {
    if (!filteredShifts) return 0;
    const completedShifts = filteredShifts.filter((shift) => shift.ended_at);
    return completedShifts.length;
  }, [filteredShifts]);

  const handleEndShift = () => {
    navigate("/shifts/confirm-end");
  };

  const handleStartShift = async () => {
    setIsRequestingLocation(true);
    try {
      const location = await getCurrentPosition();
      navigate("/shifts/confirm-start", { state: { location } });
    } finally {
      setIsRequestingLocation(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 w-full bg-gray-50">
      <div className="flex-1 overflow-y-auto">
        {/* Active Shift Section */}
        {!isLoadingOpenShift && openShift ? (
          <section className="bg-blue-500 rounded-b-3xl p-6 relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-2 w-2 bg-white rounded-full"></div>
                <span className="text-xs font-semibold text-white uppercase tracking-wide">
                  Active Shift
                </span>
              </div>
              <h2 className="text-2xl font-bold text-white m-0 mb-4">Currently Working</h2>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                <div>
                  <p className="text-white/80 text-xs mb-1">Started</p>
                  <p className="text-white text-lg font-bold">
                    {formatTimeInZone(openShift.started_at, openShift.timezone)}
                  </p>
                </div>
              </div>
              
              <Button
                onClick={handleEndShift}
                variant="outline"
                className="w-full mt-4 bg-white text-blue-600 font-semibold py-3 rounded-xl shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                <div className="h-5 w-5 border-2 border-blue-600 rounded-full mr-2"></div>
                End Shift
              </Button>
            </div>
            <div className="absolute right-0 top-0 opacity-10">
              <Clock className="h-40 w-40 text-white" />
            </div>
          </section>
        ) : null}

        {/* No Open Shift - Start Shift Banner (always at top when no active shift) */}
        {!isLoadingOpenShift && !openShift ? (
          <section className="bg-blue-50 border-b border-blue-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">You don't have an open shift.</span>
                  </p>
                </div>
              </div>
              <Button
                onClick={handleStartShift}
                disabled={isRequestingLocation}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors tap-highlight-transparent"
              >
                {isRequestingLocation ? "Getting location…" : "Start Shift"}
              </Button>
            </div>
          </section>
        ) : null}

        {/* Patrol card: show when patrol is enabled (with or without open shift) */}
        {showPatrolCard && patrolConfig && (
          <section id="qr-scan-section" className="px-5 mt-3 mb-6">
            {/* Overdue state */}
            {patrolStatus && isPatrolOverdue ? (
              <div className="bg-red-600 rounded-2xl shadow-lg border-2 border-red-700 p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-bold text-white text-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-300" />
                    Next Scan Due
                  </h3>
                  <span className="bg-white text-red-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">
                    Critical
                  </span>
                </div>
                <p className="text-white text-base font-bold mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-300" />
                  OVERDUE
                </p>
                <div className="space-y-3 mb-5">
                  <div className="bg-red-700/50 backdrop-blur-sm rounded-lg p-3 border border-red-500/30">
                    <p className="text-xs text-red-200 mb-1">Expected period</p>
                    <p className="text-sm font-semibold text-white">
                      {formatTimeInZone(patrolDisplayStartIso, patrolTz)} –{" "}
                      {formatTimeInZone(patrolDisplayEndIso, patrolTz)}
                    </p>
                    <p className="text-xs text-red-200/90 mt-2 leading-snug">
                      Patrol rule: {patrolStatusRequiredScans} scans every {patrolConfig.patrol_interval_minutes}{" "}
                      min.
                    </p>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-red-100">Patrol Progress</span>
                      <span className="text-white font-bold">
                        {patrolEffectiveScanned} of {patrolStatusRequiredScans} scanned
                      </span>
                    </div>
                    <div className="w-full bg-red-900/50 rounded-full h-2.5 border border-red-500/30">
                      <div
                        className="bg-yellow-400 h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${patrolProgressPercent}%` }}
                        role="progressbar"
                        aria-valuenow={patrolEffectiveScanned}
                        aria-valuemin={0}
                        aria-valuemax={patrolStatusRequiredScans}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handlePatrolScanQr}
                  disabled={isPatrolSubmitPending || isPatrolScanning}
                  className="w-full bg-white text-red-600 font-bold py-4 rounded-lg shadow-xl hover:bg-red-50 active:scale-[0.98] transition-all flex items-center justify-center gap-3 text-base"
                >
                  <QrCode className="h-5 w-5" />
                  {isPatrolScanning ? "Opening scanner…" : "SCAN QR CODE"}
                </Button>
              </div>
            ) : (
              // Normal / Imminent states
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-bold text-gray-800 text-lg">Next Scan Due</h3>
                  {isPatrolImminent ? (
                    <span className="bg-orange-100 text-orange-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">
                      Warning
                    </span>
                  ) : (
                    <span className="bg-red-50 text-red-500 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">
                      Priority
                    </span>
                  )}
                </div>
                {patrolStatus ? (
                  <p
                    className={
                      isPatrolImminent
                        ? "text-orange-600 text-sm font-semibold mb-4"
                        : "text-gray-500 text-sm mb-4"
                    }
                  >
                    {isPatrolImminent
                      ? "Due imminently"
                      : `You must scan ${patrolStatus.min_qr_codes_to_scan} QR codes before ${formatTimeInZone(
                          patrolDisplayEndIso,
                          patrolTz
                        )}`}
                  </p>
                ) : (
                  <p className="text-gray-500 text-sm mb-4">
                    Scan {patrolConfig.min_qr_codes_to_scan} QR codes every{" "}
                    {patrolConfig.patrol_interval_minutes} minutes
                  </p>
                )}
                <div className="space-y-3 mb-5">
                  {patrolStatus && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Current period</p>
                      <p className="text-sm font-medium text-gray-700">
                        {formatTimeInZone(patrolDisplayStartIso, patrolTz)} –{" "}
                        {formatTimeInZone(patrolDisplayEndIso, patrolTz)}
                      </p>
                      <p className="text-xs text-gray-400 mt-2 leading-snug">
                        Patrol rule: {patrolStatusRequiredScans || patrolRequiredScans} scans every{" "}
                        {patrolConfig.patrol_interval_minutes} min.
                      </p>
                    </div>
                  )}
                  <div>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-gray-500">Patrol Progress</span>
                      <span
                        className={
                          isPatrolImminent ? "text-orange-600 font-medium" : "text-blue-600 font-medium"
                        }
                      >
                        {patrolEffectiveScanned} of {patrolStatusRequiredScans || patrolRequiredScans} scanned
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={
                          isPatrolImminent
                            ? "bg-orange-500 h-2 rounded-full transition-all duration-500"
                            : "bg-blue-500 h-2 rounded-full transition-all duration-500"
                        }
                        style={{ width: `${patrolProgressPercent}%` }}
                        role="progressbar"
                        aria-valuenow={patrolEffectiveScanned}
                        aria-valuemin={0}
                        aria-valuemax={patrolStatusRequiredScans || patrolRequiredScans}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handlePatrolScanQr}
                  disabled={isPatrolSubmitPending || isPatrolScanning}
                  className={
                    isPatrolImminent
                      ? "w-full bg-orange-500 text-white font-medium py-3.5 rounded-lg shadow-md hover:bg-orange-600 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                      : "w-full bg-gray-900 text-white font-medium py-3.5 rounded-lg shadow-md hover:bg-gray-800 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                  }
                >
                  <QrCode className="h-5 w-5" />
                  {isPatrolScanning ? "Opening scanner…" : "Scan QR code"}
                </Button>
              </div>
            )}
          </section>
        )}

        {/* Filter Buttons */}
        <section className="px-4 py-3">
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setFilter("all")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                filter === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              )}
            >
              All Shifts
            </button>
            <button
              onClick={() => setFilter("week")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                filter === "week"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              )}
            >
              This Week
            </button>
            <button
              onClick={() => setFilter("month")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                filter === "month"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              )}
            >
              This Month
            </button>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="px-5 mb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-800">{totalShifts}</p>
              <p className="text-xs text-gray-500 mt-1">Total Shifts</p>
            </div>
          </div>
        </section>

        {/* Shifts List */}
        <section className="px-5">
          <ShiftsList shifts={filteredShifts || []} isLoading={isLoading} error={error} />
        </section>
      </div>
    </div>
  );
}

